<template>
  <div>
    <label class="ontos-field-label">{{ $tt('rules') }}</label>
    <!-- Uma linha por regra: a fórmula, que tem de dar Sim, e a mensagem de quem preenche -->
    <v-row v-for="(rule, index) in rules" :key="keys[index]" dense align="start">
      <v-col cols="12" md="7">
        <!-- Obrigatória, com o `*` do `.required`, como a fórmula do calculado: a linha existe para ela -->
        <ontos-formula-input ref="editors" class="required" v-bind="bindings" :label="$tt('definition.rule')"
                             :value="rule.rule" :rule="index"
                             :draft="draftWith" :result-label="resultLabel" :example="example"
                             @input="change(index, {rule: $event})"/>
      </v-col>
      <v-col cols="10" md="4">
        <!-- Opcional (decisão do usuário): sem ela, quem preenche lê a frase padrão — que aparece
             aqui como sugestão, para o designer saber o que vai sair. Sem "(opcional)" no rótulo: no
             app, opcional é o campo sem `*` -->
        <v-text-field :label="$tt('rule-message')" :placeholder="$tt('validation.rule-default')"
                      persistent-placeholder :model-value="rule.message"
                      @update:model-value="change(index, {message: $event || undefined})"/>
      </v-col>
      <v-col cols="2" md="1">
        <v-btn icon variant="text" size="small" :title="$tt('remove')" @click="remove(index)">
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-btn variant="text" prepend-icon="mdi-plus" @click="add">{{ $tt('add-rule') }}</v-btn>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, toNative} from 'vue-facing-decorator'
import IdeaProperty, {PropertyRule} from '@/types/IdeaProperty'
import AsyncFormulaInput from '@/components/design/property/asyncFormulaInput'
import {editorsValid} from '@/components/design/property/editors'
import types from '@/model/types'

/**
 * As regras de validação de uma propriedade: cada uma, uma fórmula que tem de dar Sim, com a
 * mensagem (opcional) que quem preenche lê quando não dá.
 *
 * Cada fórmula é o MESMO editor do calculado, no modo regra (`rule`): realce, erro sublinhado,
 * autocomplete com o `self`, e o veredito contra os itens de agora no lugar dos exemplos. Quem
 * confere é o servidor; o cliente não avalia regra.
 *
 * Um componente para o atributo e o relacionamento, que o usam igual.
 */
@Component({name: 'ontos-rules-input', emits: ['input'], components: {'ontos-formula-input': AsyncFormulaInput}})
class RulesInput extends Vue {
  @Prop({type: Array, default: () => []}) readonly rules!: PropertyRule[]
  /** O que o editor de fórmula recebe igual em todo formulário — ver `PropertyInput.formulaBindings`. */
  @Prop({type: Object, required: true}) readonly bindings!: Record<string, unknown>
  /** A propriedade como está no formulário, sem as regras — elas vêm do `rules`. */
  @Prop({type: Object, required: true}) readonly draft!: Partial<IdeaProperty>
  /** O que se escreve numa regra, com os nomes da coleção, enquanto ela está vazia (`formulaExamples`). */
  @Prop({type: String, default: ''}) readonly example!: string

  /**
   * Uma chave por linha, que não muda quando outra sai: com o índice como chave, tirar a primeira
   * regra deixaria o editor dela mostrando a segunda — o CodeMirror guarda o próprio texto.
   */
  keys: number[] = []
  nextKey = 0

  //Exposto ao template: toda regra dá Sim/Não, o nome que o app dá ao tipo
  resultLabel = types.boolean.name

  created() {
    this.keys = this.rules.map(() => this.nextKey++)
  }

  /** O rascunho com as regras de agora: o editor de cada uma põe o próprio texto no lugar dela. */
  get draftWith(): Partial<IdeaProperty> {
    return {...this.draft, rules: this.rules}
  }

  add() {
    this.keys.push(this.nextKey++)
    this.$emit('input', [...this.rules, {rule: ''}])
  }

  remove(index: number) {
    this.keys.splice(index, 1)
    this.$emit('input', this.rules.filter((rule, position) => position !== index))
  }

  change(index: number, changes: Partial<PropertyRule>) {
    this.$emit('input', this.rules.map((rule, position) => position === index ? {...rule, ...changes} : rule))
  }

  /**
   * Antes de salvar: toda regra confere — e a vazia que ninguém visitou passa a dizer que é
   * obrigatória, como os outros campos dizem no salvar.
   */
  validate(): boolean {
    return editorsValid(this.$refs.editors)
  }
}

export default toNative(RulesInput)
</script>
